package org.teddapp.project.ui.notes

import androidx.compose.runtime.Composable
import kotlinx.coroutines.Dispatchers
import app.cash.sqldelight.coroutines.mapToList
import app.cash.sqldelight.coroutines.asFlow
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.tedd.db.Note
import com.tedd.db.Notebook
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.drop
import kotlinx.coroutines.launch
import org.teddapp.project.db.ParentKind
import org.teddapp.project.db.TeddRepositories
import org.teddapp.project.ui.skills.joinTags
import org.teddapp.project.ui.skills.parseTags

/** Carnet dans lequel écrire, ou l'une des vues transversales. */
internal sealed interface NotesFilter {
    /** Tout ce qui existe, du plus récemment touché au plus ancien. */
    data object All : NotesFilter

    /** Les notes épinglées, quel que soit leur carnet. */
    data object Pinned : NotesFilter

    /** Les notes favorites, quel que soit leur carnet. */
    data object Favorites : NotesFilter

    /** Les notes qui portent une étiquette. */
    data class Tag(val name: String) : NotesFilter

    /** Un carnet précis. */
    data class Book(val id: String) : NotesFilter

    /** Les notes supprimées depuis moins de 30 jours. */
    data object Trash : NotesFilter
}

/**
 * Un dessin ouvert dans l'éditeur : la note qui le porte, son identifiant, le dessin
 * au moment de l'ouvrir, et le texte de la note pour en retirer la référence si le
 * dessin est supprimé.
 */
internal class DrawingSession(
    val noteId: String,
    val inkId: String,
    val initial: org.teddapp.project.ui.notes.ink.InkDrawing,
    val body: androidx.compose.foundation.text.input.TextFieldState,
    /** Le poids que ce dessin peut atteindre sans faire déborder la note. */
    val budget: Int,
)

/** L'ordre de la liste, sous les notes épinglées qui restent toujours en tête. */
internal enum class NotesSort(val label: String) {
    Modified("Dernière modification"),
    Created("Date de création"),
    Title("Titre"),
}

/**
 * État de l'écran Notes.
 *
 * Une note appartient à un carnet, ou à rien. Le corps s'enregistre tout seul, un
 * instant après la dernière frappe : une note qu'on doit penser à sauver est une note
 * qu'on perd.
 */
internal class NotesModel(
    private val repos: TeddRepositories,
    private val scope: CoroutineScope,
) {
    /** Les listes observées, chaudes cinq secondes après le départ de l'écran : y revenir ne relit rien. */
    private fun <T> hot(flow: kotlinx.coroutines.flow.Flow<List<T>>) =
        flow.stateIn(scope, SharingStarted.WhileSubscribed(org.teddapp.project.ui.ScreenModels.HOT_MS), emptyList())
    internal val notesFlow = hot(repos.notes.observeAll())
    internal val trashFlow = hot(repos.notes.observeTrash())
    internal val booksFlow = hot(repos.notebooks.observeAll())
    internal val mindMapsFlow = hot(repos.mindMaps.observeAll())
    internal val mindMapNodesFlow = hot(repos.db.mindMapNodeQueries.selectAll().asFlow().mapToList(Dispatchers.Default))

    var notes by mutableStateOf<List<Note>>(emptyList())
    var trash by mutableStateOf<List<Note>>(emptyList())
    var books by mutableStateOf<List<Notebook>>(emptyList())

    var filter by mutableStateOf<NotesFilter>(NotesFilter.All)
    var selectedId by mutableStateOf<String?>(null)
    var search by mutableStateOf("")
    var sort by mutableStateOf(NotesSort.Modified)
    var creatingBook by mutableStateOf(false)
    var renamingBook by mutableStateOf<Notebook?>(null)

    /** Note dont la suppression attend confirmation. */
    var confirmDelete by mutableStateOf<Note?>(null)

    /** Note de la corbeille dont la destruction attend confirmation. */
    var confirmForget by mutableStateOf<Note?>(null)

    /** Vider la corbeille attend confirmation. */
    var confirmEmptyTrash by mutableStateOf(false)

    val inTrash: Boolean get() = filter is NotesFilter.Trash

    /** Dossiers ouverts. Tout est replié au départ, sauf le chemin du filtre courant. */
    var expanded by mutableStateOf<Set<String>>(emptySet())

    /** Dossier dans lequel créer le prochain carnet ; null pour la racine. */
    var creatingUnder by mutableStateOf<String?>(null)

    private val live: List<Note> get() = notes.filter { it.is_deleted == 0L }

    private val liveBooks: List<Notebook> get() = books.filter { it.is_deleted == 0L }

    /**
     * Carnets de premier niveau. Un carnet dont le parent a disparu remonte à la
     * racine plutôt que de devenir invisible : perdre un dossier ne doit jamais faire
     * disparaître ce qu'il contenait.
     */
    val roots: List<Notebook>
        get() {
            val vivants = liveBooks.map { it.id }.toHashSet()
            return liveBooks.filter { it.parent_id == null || it.parent_id !in vivants }
        }

    /** Sous-dossiers directs d'un carnet, dans leur ordre. */
    fun childrenOf(bookId: String): List<Notebook> = liveBooks.filter { it.parent_id == bookId }

    /** Le carnet et tout ce qu'il contient, en profondeur. */
    private fun withDescendants(bookId: String): Set<String> {
        val acc = linkedSetOf(bookId)
        var frontier = listOf(bookId)
        // Les carnets vivants sont peu nombreux : une descente simple suffit, et la
        // borne évite de tourner en rond si un parent pointait un descendant.
        repeat(liveBooks.size) {
            frontier = frontier.flatMap { id -> childrenOf(id).map { it.id } }.filter { acc.add(it) }
            if (frontier.isEmpty()) return acc
        }
        return acc
    }

    fun toggleExpanded(bookId: String) {
        expanded = if (bookId in expanded) expanded - bookId else expanded + bookId
    }

    /** Notes du filtre courant, épinglées d'abord, puis par date de dernière touche. */
    val visible: List<Note>
        get() {
            val base = when (val f = filter) {
                NotesFilter.All -> live
                NotesFilter.Trash -> trash
                NotesFilter.Pinned -> live.filter { it.is_pinned == 1L }
                NotesFilter.Favorites -> live.filter { it.is_favorite == 1L }
                is NotesFilter.Tag -> live.filter { f.name in parseTags(it.tags) }
                // Un dossier montre aussi ce que contiennent ses sous-dossiers :
                // sinon un dossier parent paraît vide alors qu'il porte tout.
                is NotesFilter.Book -> withDescendants(f.id).let { ids -> live.filter { it.parent_id in ids } }
            }
            val q = search.trim().lowercase()
            val found = if (q.isEmpty()) base else base.filter { matches(it, q) }
            // La corbeille garde l'ordre des suppressions : la dernière jetée en haut.
            if (filter is NotesFilter.Trash) return found
            val pinnedFirst = compareByDescending<Note> { it.is_pinned }
            return found.sortedWith(
                when (sort) {
                    NotesSort.Modified -> pinnedFirst.thenByDescending { it.updated_at }
                    NotesSort.Created -> pinnedFirst.thenByDescending { it.created_at }
                    // Une note sans titre se range après les autres, pas en tête sous « ».
                    NotesSort.Title -> pinnedFirst
                        .thenBy { it.title.isNullOrBlank() }
                        .thenBy { it.title.orEmpty().trim().lowercase() }
                },
            )
        }

    /** Une note répond à la recherche par son titre, son texte, ses étiquettes ou son carnet. */
    private fun matches(note: Note, q: String): Boolean =
        note.title.orEmpty().lowercase().contains(q) ||
            // Le texte d'une note verrouillée est chiffré : on n'y cherche pas.
            (!NoteLock.isLocked(note.body) && note.body.lowercase().contains(q)) ||
            note.tags.orEmpty().lowercase().contains(q) ||
            bookName(note.parent_id).orEmpty().lowercase().contains(q)

    val selected: Note? get() = visible.firstOrNull { it.id == selectedId } ?: visible.firstOrNull()

    /** Notes d'un carnet, sous-dossiers compris : c'est ce que le dossier représente. */
    fun countIn(bookId: String): Int = withDescendants(bookId).let { ids -> live.count { it.parent_id in ids } }

    fun bookName(id: String?): String? = books.firstOrNull { it.id == id }?.name

    fun book(id: String?): Notebook? = books.firstOrNull { it.id == id }

    /** Vrai si un carnet en contient un autre : l'arbre garde alors la place des chevrons. */
    val hasNested: Boolean get() = liveBooks.any { b -> b.parent_id != null && liveBooks.any { it.id == b.parent_id } }

    /** Les étiquettes en usage, les plus portées d'abord, avec leur nombre de notes. */
    val tags: List<Pair<String, Int>>
        get() = live.flatMap { parseTags(it.tags) }
            .groupingBy { it }.eachCount()
            .entries.sortedWith(compareByDescending<Map.Entry<String, Int>> { it.value }.thenBy { it.key })
            .map { it.key to it.value }

    /**
     * Tous les carnets, dans l'ordre de l'arbre, chacun avec son chemin (« Cours › Maths »).
     * C'est la liste où ranger une note : un sous-dossier doit s'y trouver comme les autres.
     */
    fun bookPaths(): List<Pair<Notebook, String>> {
        val acc = mutableListOf<Pair<Notebook, String>>()
        val vus = mutableSetOf<String>()
        fun descend(book: Notebook, path: String) {
            if (!vus.add(book.id)) return
            acc += book to path
            childrenOf(book.id).forEach { descend(it, "$path › ${it.name}") }
        }
        roots.forEach { descend(it, it.name) }
        return acc
    }

    /** Les cartes heuristiques et leurs nœuds : pour dire d'une note « Carte : … ». */
    var mindMaps by mutableStateOf<List<com.tedd.db.Mind_map>>(emptyList())
    var mindMapNodes by mutableStateOf<List<com.tedd.db.Mind_map_node>>(emptyList())

    /** La carte qui porte cette note, si elle est écrite dans un nœud de carte. */
    fun mindMapOf(note: Note): com.tedd.db.Mind_map? {
        if (note.parent_kind != org.teddapp.project.db.ParentKind.MindMapNode.raw) return null
        val node = mindMapNodes.firstOrNull { it.id == note.parent_id } ?: return null
        return mindMaps.firstOrNull { it.id == node.map_id }
    }

    fun select(id: String) { selectedId = id }

    /**
     * Crée une note vide et l'ouvre aussitôt. Elle naît dans la vue où on l'écrit : dans
     * le carnet ouvert, avec l'étiquette filtrée, en favori depuis les favoris. Sinon
     * elle disparaîtrait de la liste au moment même où on la crée.
     */
    fun newNote() {
        val f = filter
        val bookId = (f as? NotesFilter.Book)?.id
        scope.launch {
            val id = repos.notes.create(
                parentKind = if (bookId == null) ParentKind.Standalone else ParentKind.Notebook,
                parentId = bookId,
                title = "",
                body = "",
                isPinned = f is NotesFilter.Pinned,
                isFavorite = f is NotesFilter.Favorites,
                tags = (f as? NotesFilter.Tag)?.name,
                orderIndex = 0L,
            )
            selectedId = id
        }
    }

    /**
     * Écritures en attente, une par note. Elles vivent hors de l'écran : quitter Notes
     * juste après une frappe ne doit pas perdre cette frappe.
     */
    private val writer = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private val pending = mutableMapOf<String, Job>()

    /**
     * Enregistre le titre et le corps, un instant après la dernière frappe : rien à
     * sauver à la main, et une seule écriture pour un mot tapé au lieu d'une par lettre.
     */
    fun edit(note: Note, title: String, body: String) {
        pending.remove(note.id)?.cancel()
        // Une note verrouillée ouverte se rechiffre à chaque enregistrement : le clair ne
        // quitte jamais l'écran.
        val stored = unlocked[note.id]?.let { NoteLock.reseal(it, body) } ?: body
        pending[note.id] = writer.launch {
            delay(WRITE_DELAY_MS)
            repos.notes.updateText(note.id, title, stored)
        }
    }

    // ── Verrou ───────────────────────────────────────────────────────

    /** Les notes verrouillées ouvertes à l'écran, avec leur clé. Oubliées dès qu'on les quitte. */
    var unlocked by mutableStateOf<Map<String, NoteLock.Unlocked>>(emptyMap())

    /** Ouvre une note verrouillée ; [onResult] reçoit faux si la phrase n'est pas la bonne. */
    fun unlockNote(note: Note, passphrase: String, onResult: (Boolean) -> Unit) {
        scope.launch {
            val u = kotlinx.coroutines.withContext(Dispatchers.Default) { NoteLock.unlock(passphrase, note.body) }
            if (u != null) unlocked = unlocked + (note.id to u)
            onResult(u != null)
        }
    }

    /**
     * Verrouille la note : son texte [plain] est chiffré, et ses versions passées, gardées
     * en clair sur l'appareil, sont effacées. Elle reste ouverte jusqu'à ce qu'on la quitte.
     */
    fun lockNote(note: Note, title: String, plain: String, passphrase: String, onDone: (String?) -> Unit) {
        NoteLock.refusal(plain, note.attachments)?.let { onDone(it); return }
        pending.remove(note.id)?.cancel()
        scope.launch {
            val (body, u) = kotlinx.coroutines.withContext(Dispatchers.Default) { NoteLock.lock(passphrase, plain) }
            repos.notes.updateText(note.id, title, body)
            kotlinx.coroutines.withContext(Dispatchers.Default) { repos.db.noteRevisionQueries.deleteByNote(note.id) }
            unlocked = unlocked + (note.id to u)
            snackbar.show("Note verrouillée")
            onDone(null)
        }
    }

    /** Retire le verrou : le texte redevient du clair, ici et sur le serveur. */
    fun removeLock(note: Note, title: String, plain: String) {
        pending.remove(note.id)?.cancel()
        unlocked = unlocked - note.id
        scope.launch { repos.notes.updateText(note.id, title, plain) }
        snackbar.show("Verrou retiré")
    }

    /** Referme une note verrouillée qu'on quitte. */
    fun relock(noteId: String) {
        if (noteId in unlocked) unlocked = unlocked - noteId
    }

    fun toggleFavorite(note: Note) {
        scope.launch { repos.notes.setFavorite(note.id, note.is_favorite == 0L) }
    }

    /** Ajoute une étiquette à la note ; « ML » et « ml » sont la même. */
    fun addTag(note: Note, tag: String) {
        val next = parseTags(note.tags) + parseTags(tag)
        scope.launch { repos.notes.setTags(note.id, joinTags(next.distinct())) }
    }

    fun removeTag(note: Note, tag: String) {
        scope.launch { repos.notes.setTags(note.id, joinTags(parseTags(note.tags) - tag)) }
    }

    /** Les étiquettes déjà connues qui commencent comme [typed] et que la note n'a pas. */
    fun suggestTags(note: Note, typed: String): List<String> {
        val q = typed.trim().lowercase()
        if (q.isEmpty()) return emptyList()
        val mine = parseTags(note.tags).toSet()
        return tags.map { it.first }.filter { it.startsWith(q) && it != q && it !in mine }.take(5)
    }

    fun setColor(note: Note, color: String?) {
        scope.launch { repos.notes.setColor(note.id, color) }
    }

    /** Copie la note et ouvre la copie. */
    fun duplicate(note: Note) {
        scope.launch { selectedId = repos.notes.duplicate(note) }
    }

    /** Les messages de l'écran (une image trop lourde, pas de sélecteur…). */
    val snackbar = org.teddapp.project.ui.TeddSnackbarState()

    /** L'image ouverte en grand : la note, l'image, et le texte d'où la retirer. */
    var viewing by mutableStateOf<Triple<String, String, androidx.compose.foundation.text.input.TextFieldState>?>(null)

    val db: com.tedd.db.TeddDatabase get() = repos.db

    /** Choisit une image sur l'appareil et la pose au curseur. */
    fun addImage(note: Note, body: androidx.compose.foundation.text.input.TextFieldState) {
        scope.launch { pickImageInto(repos.db, note, body)?.let { snackbar.show(it, 3500) } }
    }

    /** Retire l'image ouverte de la note, et de l'appareil si la note la possède. */
    fun removeViewedImage() {
        val (noteId, id, body) = viewing ?: return
        body.format { t, s, e -> MdEdit(MarkdownEdits.removeFile(t, id), s, e) }
        writer.launch { NoteImages.remove(repos.db, noteId, id) }
        viewing = null
    }

    // ── Export et import ─────────────────────────────────────────────

    /** Le PDF n'existe que là où la plateforme sait l'écrire. */
    val canExportPdf: Boolean get() = org.teddapp.project.portfolio.export.PortfolioExporters.savePdf != null
    val canImport: Boolean get() = NoteImportPicker.pick != null

    /**
     * La note en Markdown : enregistrée dans un fichier si la plateforme en choisit un,
     * sinon confiée au partage, sinon copiée par [copy].
     */
    fun exportMarkdown(note: Note, copy: (String) -> Unit) {
        val text = NoteExport.markdown(note)
        scope.launch {
            val save = org.teddapp.project.portfolio.export.PortfolioExporters.saveMarkdown
            val share = org.teddapp.project.card.TeddShare.shareText
            when {
                save != null -> runCatching { save(NoteExport.fileName(note, ".md"), text) }
                    .onSuccess { path -> path?.let { snackbar.show("Note enregistrée : ${it.substringAfterLast('/')}", 3000) } }
                    .onFailure { snackbar.show("L'export a échoué.") }
                share != null -> share(note.title.orEmpty().ifBlank { "Note" }, text)
                else -> { copy(text); snackbar.show("Note copiée en Markdown") }
            }
        }
    }

    fun exportPdf(note: Note) {
        val save = org.teddapp.project.portfolio.export.PortfolioExporters.savePdf ?: return
        val date = org.teddapp.project.ui.TeddDates.formatShort(org.teddapp.project.ui.TeddDates.isoFromDays((org.teddapp.project.db.nowMillis() / 86_400_000L).toInt())) ?: ""
        val doc = NoteExport.document(note, bookName(note.parent_id), date)
        scope.launch {
            runCatching { save(NoteExport.fileName(note, ".pdf"), doc) }
                .onSuccess { path -> path?.let { snackbar.show("PDF enregistré : ${it.substringAfterLast('/')}", 3000) } }
                .onFailure { snackbar.show("L'export PDF a échoué.") }
        }
    }

    /** Importe un fichier Markdown en note, dans le carnet ouvert, et l'ouvre. */
    fun importMarkdown() {
        val pick = NoteImportPicker.pick ?: return
        val bookId = (filter as? NotesFilter.Book)?.id
        scope.launch {
            val (name, text) = runCatching { pick() }.getOrNull() ?: return@launch
            val (title, body) = NoteExport.fromMarkdown(name, text)
            val id = repos.notes.create(
                parentKind = if (bookId == null) ParentKind.Standalone else ParentKind.Notebook,
                parentId = bookId,
                title = title,
                body = body,
            )
            openNote(id)
            snackbar.show("« $title » importée")
        }
    }

    // ── Liens entre notes ────────────────────────────────────────────

    /** Les titres des notes vivantes : de quoi compléter un `[[`. */
    val titles: List<String> get() = live.mapNotNull { it.title?.takeIf { t -> t.isNotBlank() } }

    /** La note de ce titre, ou null. Deux notes du même titre : la plus récemment touchée. */
    fun noteTitled(title: String): Note? {
        val k = NoteLinks.key(title)
        return live.filter { NoteLinks.key(it.title.orEmpty()) == k }.maxByOrNull { it.updated_at }
    }

    /** Les notes qui citent [note] par un lien. */
    fun backlinks(note: Note): List<Note> {
        val title = note.title.orEmpty()
        if (title.isBlank()) return emptyList()
        return live.filter { it.id != note.id && !NoteLock.isLocked(it.body) && NoteLinks.mentions(it.body, title) }
            .sortedByDescending { it.updated_at }
    }

    /** Une note à ouvrir, demandée d'ailleurs (un lien) : le téléphone la suit aussi. */
    var openRequest by mutableStateOf<String?>(null)

    /** Ouvre la note [id], en élargissant la vue si le filtre courant la cache. */
    fun openNote(id: String) {
        if (visible.none { it.id == id }) {
            filter = NotesFilter.All
            search = ""
        }
        selectedId = id
        openRequest = id
    }

    /** Suit un lien : ouvre la note de ce titre, ou la crée si elle n'existe pas encore. */
    fun followLink(title: String) {
        noteTitled(title)?.let { openNote(it.id); return }
        scope.launch {
            val id = repos.notes.create(ParentKind.Standalone, body = "", title = title.trim())
            openNote(id)
        }
    }

    /** Le dessin ouvert dans l'éditeur, ou null. */
    var drawing by mutableStateOf<DrawingSession?>(null)

    private fun budgetFor(note: Note, inkId: String): Int {
        val inks = org.teddapp.project.ui.notes.ink.NoteAttachments.inks(note.attachments)
        val others = inks.filterKeys { it != inkId }.values.sumOf { it.length }
        return org.teddapp.project.ui.notes.ink.NoteAttachments.MAX_INK_CHARS - others
    }

    /** Pose un dessin vide au curseur et l'ouvre. */
    fun startDrawing(note: Note, body: androidx.compose.foundation.text.input.TextFieldState) {
        val id = org.teddapp.project.db.newId("ink")
        body.format { t, s, _ -> MarkdownEdits.block(t, s, MarkdownEdits.inkRef(id)) }
        drawing = DrawingSession(note.id, id, org.teddapp.project.ui.notes.ink.InkDrawing.Empty, body, budgetFor(note, id))
    }

    /** Ouvre un dessin déjà posé dans la note. */
    fun openDrawing(note: Note, inkId: String, body: androidx.compose.foundation.text.input.TextFieldState) {
        val data = org.teddapp.project.ui.notes.ink.NoteAttachments.inks(note.attachments)[inkId]
        val d = data?.let { org.teddapp.project.ui.notes.ink.InkCodec.decode(it) } ?: org.teddapp.project.ui.notes.ink.InkDrawing.Empty
        drawing = DrawingSession(note.id, inkId, d, body, budgetFor(note, inkId))
    }

    /** Enregistre le dessin dans la note ; un dessin vide n'y laisse rien. */
    fun saveDrawing(session: DrawingSession, d: org.teddapp.project.ui.notes.ink.InkDrawing) {
        val data = if (d.isEmpty) null else org.teddapp.project.ui.notes.ink.InkCodec.encode(d)
        writer.launch {
            repos.notes.updateAttachments(session.noteId) { raw ->
                org.teddapp.project.ui.notes.ink.NoteAttachments.withInk(raw, session.inkId, data)
            }
        }
    }

    /** Ferme l'éditeur. Un dessin resté vide disparaît, sa référence dans le texte aussi. */
    fun closeDrawing(session: DrawingSession, d: org.teddapp.project.ui.notes.ink.InkDrawing) {
        saveDrawing(session, d)
        if (d.isEmpty) session.body.format { t, s, e -> MdEdit(MarkdownEdits.removeInk(t, session.inkId), s, e) }
        drawing = null
    }

    fun deleteDrawing(session: DrawingSession) = closeDrawing(session, session.initial.with(emptyList()))

    /**
     * Le texte lu dans le dessin, posé dans la note : sous le dessin, ou à sa place
     * ([replace]) — le dessin disparaît alors.
     */
    fun placeReadText(session: DrawingSession, d: org.teddapp.project.ui.notes.ink.InkDrawing, text: String, replace: Boolean) {
        session.body.format { t, s, e -> MdEdit(MarkdownEdits.besideInk(t, session.inkId, text, replace), s, e) }
        closeDrawing(session, if (replace) d.with(emptyList()) else d)
    }

    /**
     * Retire les dessins que le texte ne cite plus : leur ligne a été effacée lors d'une
     * séance précédente. Appelé à l'ouverture d'une note, jamais pendant qu'on l'écrit :
     * une ligne coupée pour être collée plus loin ne doit pas perdre son dessin.
     */
    fun pruneOrphanInks(note: Note) {
        val orphans = org.teddapp.project.ui.notes.ink.NoteAttachments.orphanInks(note.attachments, note.body)
        if (orphans.isEmpty()) return
        writer.launch {
            repos.notes.updateAttachments(note.id) { raw ->
                orphans.fold(raw) { acc, id -> org.teddapp.project.ui.notes.ink.NoteAttachments.withInk(acc, id, null) }
            }
        }
    }

    fun togglePin(note: Note) {
        scope.launch { repos.notes.setPinned(note.id, note.is_pinned == 0L) }
    }

    /** Déplace la note vers un carnet, ou l'en sort si [bookId] est nul. */
    fun moveTo(note: Note, bookId: String?) {
        scope.launch {
            repos.notes.setParent(
                id = note.id,
                parentKind = if (bookId == null) ParentKind.Standalone else ParentKind.Notebook,
                parentId = bookId,
            )
        }
    }

    /** Met la note à la corbeille, où elle reste 30 jours. */
    fun deleteNote(note: Note) {
        // Une frappe en attente sur une note qu'on jette n'a plus à s'écrire.
        pending.remove(note.id)?.cancel()
        scope.launch { repos.notes.softDelete(note.id) }
        if (selectedId == note.id) selectedId = null
        confirmDelete = null
    }

    fun restoreNote(note: Note) {
        scope.launch { repos.notes.restore(note.id) }
        if (selectedId == note.id) selectedId = null
    }

    fun forgetNote(note: Note) {
        scope.launch { repos.notes.deleteForever(note.id) }
        if (selectedId == note.id) selectedId = null
        confirmForget = null
    }

    fun emptyTrash() {
        scope.launch { repos.notes.emptyTrash() }
        selectedId = null
        confirmEmptyTrash = false
    }

    fun createBook(name: String, icon: String, color: String) {
        if (name.isBlank()) return
        val parent = creatingUnder
        scope.launch {
            repos.notebooks.create(
                name = name.trim(),
                icon = icon,
                color = color,
                parentId = parent,
                orderIndex = books.size.toLong(),
            )
        }
        if (parent != null) expanded = expanded + parent
        creatingBook = false
        creatingUnder = null
    }

    /** Nom du dossier dans lequel la création aura lieu, ou null à la racine. */
    fun creatingUnderName(): String? = creatingUnder?.let { id -> bookName(id) }

    /** Renomme le carnet et change son icône ou sa couleur. */
    fun updateBook(book: Notebook, name: String, icon: String, color: String) {
        if (name.isBlank()) return
        scope.launch {
            repos.notebooks.update(
                id = book.id, name = name.trim(), icon = icon, color = color,
                parentId = book.parent_id, orderIndex = book.order_index,
            )
        }
        renamingBook = null
    }

    /**
     * Supprime un carnet. Ses notes ne sont pas détruites : elles sortent du carnet
     * et restent accessibles depuis « Toutes ». Perdre un carnet ne doit jamais
     * vouloir dire perdre ce qu'on y avait écrit.
     */
    fun deleteBook(book: Notebook) {
        scope.launch {
            // Les notes du dossier remontent à la racine ; ses sous-dossiers aussi,
            // et gardent les leurs. On supprime un rangement, pas un contenu.
            live.filter { it.parent_id == book.id }.forEach { moveTo(it, null) }
            childrenOf(book.id).forEach { child ->
                repos.notebooks.update(
                    id = child.id, name = child.name, icon = child.icon,
                    color = child.color, parentId = null, orderIndex = child.order_index,
                )
            }
            repos.notebooks.softDelete(book.id)
        }
        if (filter == NotesFilter.Book(book.id)) filter = NotesFilter.All
        renamingBook = null
    }

    companion object {
        private const val WRITE_DELAY_MS = 300L

        /** Les couleurs d'une note : de quoi la retrouver d'un coup d'œil, pas un arc-en-ciel. */
        val NoteColors = listOf("#EF4444", "#F59E0B", "#10B981", "#0EA5E9", "#6366F1", "#EC4899")

        /** Les icônes d'un carnet. */
        val BookIcons = listOf("book", "notes", "spark", "target", "heart", "flame", "home", "users", "wallet", "calendar", "list", "focus")
    }
}

@Composable
internal fun rememberNotesModel(repos: TeddRepositories): NotesModel {
    // Gardé d'une rubrique à l'autre, avec ses listes chaudes : revenir ne relit rien.
    val model = org.teddapp.project.ui.rememberScreenModel(repos, "notes") { s -> NotesModel(repos, s) }
    model.notes = model.notesFlow.collectAsState().value
    model.trash = model.trashFlow.collectAsState().value
    // Ce qui dort dans la corbeille depuis plus de 30 jours part pour de bon.
    androidx.compose.runtime.LaunchedEffect(repos) { repos.notes.purgeTrash() }
    model.books = model.booksFlow.collectAsState().value
    model.mindMaps = model.mindMapsFlow.collectAsState().value
    model.mindMapNodes = model.mindMapNodesFlow.collectAsState().value
    return model
}

/**
 * La note ouverte, telle qu'on l'écrit : son titre, son texte avec leur historique
 * d'annulation, et le mode, lecture ou écriture. Elle vit au-dessus de l'éditeur pour
 * que la barre de mise en forme puisse se poser ailleurs que dans la page qui défile.
 */
@androidx.compose.runtime.Stable
internal class NoteDraft(title: String, body: String, writing: Boolean) {
    var title by mutableStateOf(title)
    val body = androidx.compose.foundation.text.input.TextFieldState(body)
    var writing by mutableStateOf(writing)
}

/** Le brouillon de [note], enregistré tout seul à chaque changement de titre ou de texte. */
@Composable
internal fun rememberNoteDraft(note: Note, model: NotesModel): NoteDraft {
    // Une note qu'on ouvre, on vient la lire ; une note vide, on vient l'écrire.
    // Une note verrouillée ouverte s'écrit en clair ; le brouillon change quand on l'ouvre.
    val open = model.unlocked[note.id]
    val draft = remember(note.id, open != null) {
        NoteDraft(note.title.orEmpty(), open?.plain ?: note.body, writing = (open?.plain ?: note.body).isBlank())
    }
    androidx.compose.runtime.LaunchedEffect(note.id) {
        model.pruneOrphanInks(note)
        runCatching { NoteImages.pruneOrphans(model.db, note) }
    }
    androidx.compose.runtime.LaunchedEffect(draft) {
        androidx.compose.runtime.snapshotFlow { draft.title to draft.body.text.toString() }
            .drop(1)
            .collect { (title, body) -> model.edit(note, title, body) }
    }
    return draft
}
